/**
 * Home Mappers
 * Converts API DTOs (FileStorageResponseDto, DocumentListItemDto) to Home display types.
 *
 * Visibility rule:
 *  - A file is shown as a file-level row only when it has NO visible documents (still processing).
 *  - When a file HAS documents, only the documents are shown (not the file row).
 *  - The only documents excluded are those with status === Deleted.
 *
 * Mapping table (DocumentStatusEnum + isProcessingEndStateReached + currentStepCode + errorCode):
 *
 *  errorCode = BizDuplicateFile | BizDuplicateDocument  → duplicate  (takes priority)
 *  errorCode (other)                                    → error      (takes priority)
 *  Pending                                              → queued
 *  Parsed + !endState + vendor_lookup                    → finding_vendor
 *  Parsed + !endState + receipt_lookup                   → finding_receipts
 *  Parsed + !endState + match                            → matching
 *  Parsed + !endState + (other)                          → processing
 *  Parsed + endState + review*                          → review_required
 *  Parsed + endState + alert*                           → alert
 *  Parsed + endState + mismatch*                        → mismatch
 *  Parsed + endState + (none/other)                     → ready
 *  Queued                                               → queued
 *  Exported                                             → exported
 *  Error / Rejected / Deleted                           → error
 */
import { ExceptionCodeEnum } from "@/types/api/common";
import { type DocumentListItemDto, DocumentStatusEnum } from "@/types/api/documents";
import type { FileStorageResponseDto } from "@/types/api/files";
import type { HomeDocument, HomeDocumentStatus, HomeSourceFile } from "@/types/home";

/** Error codes that indicate a duplicate (shown as the "duplicate" status, not generic "error"). */
const DUPLICATE_ERROR_CODES = new Set<string>([ExceptionCodeEnum.BizDuplicateFile, ExceptionCodeEnum.BizDuplicateDocument]);

// ---------------------------------------------------------------------------
// Status mapping
// ---------------------------------------------------------------------------

export function mapDocumentStatus(
    status: DocumentStatusEnum,
    isProcessingEndStateReached: boolean,
    currentStepCode?: string | null,
    errorCode?: string,
): HomeDocumentStatus {
    // Duplicate error codes surface as "duplicate", not generic "error"
    if (errorCode && DUPLICATE_ERROR_CODES.has(errorCode)) return "duplicate";
    // All other error codes take priority and show as "error"
    if (errorCode) return "error";

    switch (status) {
        case DocumentStatusEnum.Pending:
            return "queued";

        case DocumentStatusEnum.Parsed: {
            if (!isProcessingEndStateReached) {
                // Still in workflow — map by step code
                if (currentStepCode === "vendor_lookup") return "finding_vendor";
                if (currentStepCode === "receipt_lookup") return "finding_receipts";
                if (currentStepCode === "match") return "matching";
                return "processing";
            }
            // Workflow complete — map by step code
            if (currentStepCode?.includes("review")) return "review_required";
            if (currentStepCode?.includes("alert")) return "alert";
            if (currentStepCode?.includes("mismatch")) return "mismatch";
            return "ready";
        }

        case DocumentStatusEnum.Queued:
            return "queued";

        case DocumentStatusEnum.Exported:
            return "exported";

        case DocumentStatusEnum.Error:
        case DocumentStatusEnum.Rejected:
        case DocumentStatusEnum.Deleted:
            return "error";

        default:
            return "processing";
    }
}

// ---------------------------------------------------------------------------
// Document mapping
// ---------------------------------------------------------------------------

export function mapDocumentListItem(doc: DocumentListItemDto, fileStorageId: number): HomeDocument {
    return {
        id: doc.id,
        vendorName: doc.generatedName,
        amount: 0, // Populated from DocumentDetailDto.customFieldValues in review panel
        confidence: 0, // Populated from DocumentDetailDto in review panel
        documentType: doc.documentType ?? "Unknown",
        documentDate: doc.createdAt,
        status: mapDocumentStatus(doc.status, doc.isProcessingEndStateReached, doc.currentStepCode, doc.errorCode),
        apiStatus: doc.status,
        sourceFileId: fileStorageId,
        fileStorageId,
        clientId: doc.clientId,
        errorCode: doc.errorCode,
        isExportFailed: doc.isExportFailed ?? false,
        isProcessingEndStateReached: doc.isProcessingEndStateReached,
        hasAllRequiredFields: doc.hasAllRequiredFields ?? true,
        hasFieldErrors: doc.hasFieldErrors ?? false,
        isInbound: doc.isInbound,
        isRetentionExpired: doc.isRetentionExpired ?? false,
    };
}

// ---------------------------------------------------------------------------
// File → SourceFile mapping
// ---------------------------------------------------------------------------

export function mapFileToSourceFile(file: FileStorageResponseDto): HomeSourceFile {
    const orgDoc = file.documents?.find((d) => d.organizationId != null);
    const visibleDocs = (file.documents ?? []).filter((doc) => doc.status !== DocumentStatusEnum.Deleted).map((doc) => mapDocumentListItem(doc, file.id));

    // A file with an errorCode and no documents must still appear as an error (or duplicate) row.
    if (visibleDocs.length === 0 && file.errorCode) {
        const baseName = file.fileName.replace(/\.[^.]+$/, "") || file.fileName;
        const uiStatus: HomeDocumentStatus = DUPLICATE_ERROR_CODES.has(file.errorCode) ? "duplicate" : "error";
        const errorDoc: HomeDocument = {
            id: -file.id,
            vendorName: baseName,
            amount: 0,
            confidence: 0,
            documentType: uiStatus === "duplicate" ? "Duplicate" : "Error",
            documentDate: file.uploadedAt,
            status: uiStatus,
            apiStatus: "Error",
            sourceFileId: file.id,
            fileStorageId: file.id,
            errorCode: file.errorCode,
            isProcessingEndStateReached: true,
            hasAllRequiredFields: true,
            hasFieldErrors: false,
        };
        return {
            id: file.id,
            fileName: file.fileName,
            documents: [errorDoc],
            organizationId: file.organizationId ?? orgDoc?.organizationId,
            organizationName: file.organizationName ?? orgDoc?.organizationName,
            fileStatus: file.status,
        };
    }

    return {
        id: file.id,
        fileName: file.fileName,
        documents: visibleDocs,
        organizationId: file.organizationId ?? orgDoc?.organizationId,
        organizationName: file.organizationName ?? orgDoc?.organizationName,
        fileStatus: file.status,
    };
}
